// Автоотправка отчётов (Алексей 27.10 #3005): галка + время, только суперюзеру. Включение — через
// модалку, где «Включить» становится активной не сразу; при красных отметках по отменам на полках —
// замок. День с аномальными расхождениями модели и валидаторов уходит на проверку с пояснением.
import { useEffect, useState } from 'react';
import { Loader2, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useAppState } from '../app/appContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { extractApiError } from '@/utils/apiError';
import { putReportAutoSend, useReportAutoSend } from '@/api/reportAutoSend';

const CONFIRM_DELAY_S = 5;

export default function ReportAutoSendCard() {
  const { user } = useCurrentUser();
  const { zone } = useAppState();
  const zoneId = zone.selectedZoneId;
  const isSuper = user?.is_superuser === true;
  const { data, mutate } = useReportAutoSend(zoneId, isSuper);
  const [time, setTime] = useState('10:00');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [left, setLeft] = useState(CONFIRM_DELAY_S);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (data) setTime(data.send_time); }, [data]);
  useEffect(() => {
    if (!confirmOpen) return;
    setLeft(CONFIRM_DELAY_S);
    const t = setInterval(() => setLeft((x) => (x > 0 ? x - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [confirmOpen]);

  if (!isSuper || !zoneId || !data) return null;

  async function save(body: { enabled?: boolean; send_time?: string }) {
    setBusy(true); setError(null);
    try {
      const r = await putReportAutoSend(zoneId!, body);
      await mutate(r.data, { revalidate: false });
      setConfirmOpen(false);
    } catch (e) {
      setError(extractApiError(e, 'Не удалось сохранить'));
    } finally {
      setBusy(false);
    }
  }

  const locked = data.locked && !data.enabled;
  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Отправлять отчёты автоматически
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-normal uppercase tracking-wide text-muted-foreground">суперюзер</span>
        </CardTitle>
        <CardDescription>Отчёт за вчера уйдёт клиенту сам в указанное время, без валидатора. {data.rule}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <Switch checked={data.enabled} disabled={busy || locked}
            onCheckedChange={(on) => (on ? setConfirmOpen(true) : void save({ enabled: false }))} />
          <span className="text-sm">{data.enabled ? 'Включено' : 'Выключено'}</span>
          {locked && <Lock className="size-4 text-red-600" aria-label="Замок" />}
          <span className="ml-2 text-sm text-muted-foreground">в</span>
          <Input type="time" className="h-9 w-[6.5rem]" value={time} disabled={busy}
            onChange={(e) => setTime(e.target.value)}
            onBlur={() => { if (time && time !== data.send_time) void save({ send_time: time }); }} />
          <span className="text-xs text-muted-foreground">по времени точки</span>
          {busy && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        </div>
        {locked && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-800 dark:text-red-200">
            <div className="font-medium">Замок: валидаторы часто не согласны с моделью — сначала откалибровать</div>
            <ul className="mt-1 list-disc pl-5">{data.lock_reasons.map((r) => <li key={r}>{r}</li>)}</ul>
          </div>
        )}
        {data.changed_by && (
          <p className="text-xs text-muted-foreground">
            Изменил {data.changed_by}{data.changed_at ? `, ${new Date(data.changed_at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
          </p>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Включить автоотправку отчётов?</DialogTitle>
            <DialogDescription>
              Каждый день в {time} по времени точки отчёт за вчера уйдёт клиенту без проверки валидатором.
              Ошибки модели (лишняя просадка, неверная очередь) клиент увидит как есть. {data.rule}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Отмена</Button>
            <Button disabled={busy || left > 0} onClick={() => void save({ enabled: true, send_time: time })}>
              {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
              {left > 0 ? `Включить (${left})` : 'Включить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
